import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Authorize,
  Badge,
  GlobalButton,
  NavigateLinks,
  TextInput,
  WhatsAppInputAdornment,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Edit as EditIcon,
  Send as SendIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';

import { formatDate } from '@/utils/dates';
import { UserRoles } from '../../components';
import { childrenUsersPageBreadcrumbs } from '../../config';
import { useUser, useUserActions } from '../../hooks';
import { formatUserPhone } from '../../utils/formatUserPhone';

const ViewUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isLoading, error } = useUser({ userId: id || '' });
  const { onSendInvitation, onSendOtpByEmail, isSendingInvitation, isSendingOtpByEmail } =
    useUserActions();

  const breadcrumbs = useMemo(
    () => childrenUsersPageBreadcrumbs(navigate),
    [navigate]
  );

  if (!id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          El ID del usuario es requerido
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-gray-500 py-8'>Cargando...</div>
      </section>
    );
  }

  if (error || !user?.id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Usuario' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          Error al cargar el usuario o usuario no encontrado
        </div>
      </section>
    );
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim();
  const isActive = user.isActive ?? user.active ?? false;

  return (
    <section className='flex flex-col gap-6 w-full h-full'>
      <NavigateLinks
        actualPage={fullName || 'Ver usuario'}
        breadcrumbs={breadcrumbs}
      />
      <Authorize permission={BUSINESS_ROLE_GROUPS.management}>
        <div className='flex justify-end mb-4 gap-3'>
          {!isActive && (
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={() => onSendInvitation(user)}
              disabled={isSendingInvitation}
            >
              <SendIcon />
              {isSendingInvitation ? 'Enviando...' : 'Enviar invitación'}
            </GlobalButton>
          )}
          {!isActive && (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => onSendOtpByEmail(user.id)}
              disabled={isSendingOtpByEmail}
            >
              <MarkEmailReadIcon />
              {isSendingOtpByEmail
                ? 'Enviando código...'
                : 'Enviar código OTP por email'}
            </GlobalButton>
          )}
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate(`/business/users/${user.id}/edit`)}
          >
            <EditIcon />
            Editar
          </GlobalButton>
        </div>
      </Authorize>
      <div className='w-full flex flex-col lg:flex-row gap-6'>
        <div className='flex-1 min-w-0 flex flex-col gap-6'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
              <h2 className='text-xl font-bold text-gray-800'>
                Información del usuario
              </h2>
              <div className='w-fit'>
                <Badge
                  value={isActive ? 'Activo' : 'Inactivo'}
                  backgroundColor={isActive ? '#dcfce7' : '#f3f4f6'}
                  textColor={isActive ? '#166534' : '#374151'}
                  showBall={false}
                />
              </div>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <TextInput
                name='user-firstName'
                placeholder='Nombre'
                value={user.firstName}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-lastName'
                placeholder='Apellido'
                value={user.lastName}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-email'
                placeholder='Correo electrónico'
                value={user.email}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-phone'
                placeholder='Teléfono'
                value={formatUserPhone(user.phoneNumber)}
                onChange={() => {}}
                disabled
                endAdornment={
                  <WhatsAppInputAdornment
                    phone={user.phoneNumber}
                    aria-label='Abrir WhatsApp (teléfono)'
                  />
                }
              />
              <TextInput
                name='user-createdAt'
                placeholder='Fecha de creación'
                value={formatDate(user.createdAt)}
                onChange={() => {}}
                disabled
              />
            </div>
          </div>
        </div>

        <div className='w-full lg:max-w-md'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <h2 className='text-xl font-bold text-gray-800 mb-4'>Roles</h2>
            <UserRoles userRoles={user.userRoles} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ViewUser;
